import { useEffect, useState, useMemo, useRef } from "react";
import api, { API_BASE } from "@/lib/api";
import { PageHeader, fmtEUR } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, AlertTriangle, Pencil, Upload, Download, FileSpreadsheet } from "lucide-react";
import { useLists } from "@/hooks/useLists";
import { useAuth } from "@/context/AuthContext";

const DEFAULT_CATEGORIES = ["caffetteria", "bevande analcoliche", "birre", "vini", "liquori", "cocktail", "cucina", "freschi", "congelati", "dispensa", "pulizia", "packaging", "materiali di consumo"];

export default function Inventory() {
  const { lists } = useLists();
  // FASE 7.4 STEP E.1 — inventory action guards.
  const { can } = useAuth();
  const canWrite = can("inventory.write");
  const canBulkReplace = can("inventory.bulk_replace");
  const CATEGORIES = lists.inventory_categories?.length ? lists.inventory_categories : DEFAULT_CATEGORIES;
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const emptyForm = { name: "", category: "caffetteria", brand: "", supplier_id: "", quantity: 0, unit: "pz", pack_size: 1, pack_cost: 0, unit_cost: 0, min_stock: 0, ideal_stock: 0, location: "", vat: 22, expiry_date: "", notes: "", safety_days: "", target_days: "" };
  const [form, setForm] = useState(emptyForm);
  const [reorderSettings, setReorderSettings] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importMode, setImportMode] = useState("append");
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const importInputRef = useRef();

  const load = async () => {
    const [i, s, rs] = await Promise.all([api.get("/inventory"), api.get("/suppliers"), api.get("/reorder/settings").catch(() => ({ data: null }))]);
    setItems(i.data); setSuppliers(s.data); setReorderSettings(rs.data);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => items.filter((p) => {
    if (cat !== "all" && p.category !== cat) return false;
    if (q && !p.name.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [items, cat, q]);

  const openNew = () => { setEditing(null); setForm({ ...emptyForm, category: CATEGORIES[0] || "caffetteria" }); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || "", category: p.category || CATEGORIES[0], brand: p.brand || "",
      supplier_id: p.supplier_id || "", quantity: p.quantity ?? 0, unit: p.unit || "pz",
      pack_size: p.pack_size ?? 1, pack_cost: p.pack_cost ?? 0, unit_cost: p.unit_cost ?? 0,
      min_stock: p.min_stock ?? 0, ideal_stock: p.ideal_stock ?? 0, location: p.location || "",
      vat: p.vat ?? 22, expiry_date: p.expiry_date ? p.expiry_date.slice(0, 10) : "",
      notes: p.notes || "",
      safety_days: p.safety_days ?? "",
      target_days: p.target_days ?? "",
    });
    setDialogOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      quantity: Number(form.quantity), pack_size: Number(form.pack_size),
      pack_cost: Number(form.pack_cost), unit_cost: Number(form.unit_cost),
      min_stock: Number(form.min_stock), ideal_stock: Number(form.ideal_stock),
      vat: Number(form.vat),
      last_purchase_price: Number(form.unit_cost),
      avg_purchase_price: Number(form.unit_cost),
    };
    // Reorder override fields are handled via a dedicated PATCH so we can send
    // null (inherit) explicitly — never persist an "inherited" copy on the product.
    delete payload.safety_days;
    delete payload.target_days;
    try {
      let id;
      if (editing) { await api.put(`/inventory/${editing.id}`, payload); id = editing.id; }
      else { const r = await api.post("/inventory", payload); id = r.data.id; }
      // Save override (null = eredita)
      await api.patch(`/inventory/${id}/reorder-override`, {
        safety_days: form.safety_days === "" ? null : Number(form.safety_days),
        target_days: form.target_days === "" ? null : Number(form.target_days),
      });
      toast.success(editing ? "Prodotto aggiornato" : "Prodotto aggiunto");
      setDialogOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };

  const resetOverride = async () => {
    if (!editing) return;
    try {
      await api.delete(`/inventory/${editing.id}/reorder-override`);
      setForm({ ...form, safety_days: "", target_days: "" });
      toast.success("Override rimosso — usa impostazione categoria/globale");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };

  // Effective values for the currently edited product (categoria → globale)
  const effectiveReorder = useMemo(() => {
    if (!reorderSettings) return null;
    const defs = reorderSettings.defaults;
    const cat = (reorderSettings.categories || []).find((c) => c.category === form.category);
    const catSafety = cat?.safety_days_configured;
    const catTarget = cat?.target_days_configured;
    const safety = catSafety != null ? catSafety : defs.effective_safety_days;
    const target = catTarget != null ? catTarget : defs.effective_target_days;
    return {
      safety, target,
      safety_source: catSafety != null ? "categoria" : "globale",
      target_source: catTarget != null ? "categoria" : "globale",
    };
  }, [reorderSettings, form.category]);

  const del = async (id) => {
    if (!window.confirm("Eliminare?")) return;
    await api.delete(`/inventory/${id}`);
    load();
  };

  const inlineUpdate = async (p, patch) => {
    try {
      await api.put(`/inventory/${p.id}`, { ...p, ...patch });
      toast.success("Aggiornato");
      load();
    } catch { toast.error("Errore"); }
  };

  const downloadTemplate = () => {
    fetch(`${API_BASE}/inventory/import-template`, { credentials: "include" })
      .then((r) => r.blob())
      .then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = url; a.download = "inventory_template.csv"; a.click();
        URL.revokeObjectURL(url);
      });
  };

  const runImport = async () => {
    if (!importFile) { toast.error("Seleziona un file"); return; }
    setImporting(true); setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      fd.append("mode", importMode);
      const res = await fetch(`${API_BASE}/inventory/bulk-import`, { method: "POST", body: fd, credentials: "include" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Errore import");
      setImportResult(data);
      toast.success(`${data.imported} nuovi · ${data.updated || 0} aggiornati${data.errors?.length ? ` · ${data.errors.length} avvisi` : ""}`);
      load();
    } catch (err) { toast.error(String(err.message || err)); }
    finally { setImporting(false); }
  };

  const closeImport = () => {
    setImportOpen(false); setImportFile(null); setImportResult(null); setImportMode("append");
    if (importInputRef.current) importInputRef.current.value = "";
  };

  const belowMin = (p) => Number(p.quantity) < Number(p.min_stock);
  const totalValue = items.reduce((s, p) => s + Number(p.quantity) * Number(p.unit_cost), 0);

  return (
    <div data-testid="inventory-page">
      <PageHeader eyebrow="Magazzino" title="Prodotti & scorte"
        subtitle={`Valore totale magazzino: ${fmtEUR(totalValue)}`}
        actions={
          <div className="flex gap-2 flex-wrap">
            {canWrite && (
            <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="import-inv-btn">
              <Upload size={14} strokeWidth={1.5} /> Importa CSV/Excel
            </Button>
            )}
            {canWrite && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-product-btn" onClick={openNew} className="bg-neutral-900 hover:bg-neutral-800 text-[#FDFBF7]"><Plus size={14} strokeWidth={1.5} /> Nuovo prodotto</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-serif">{editing ? "Modifica prodotto" : "Nuovo prodotto"}</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Nome</Label><Input data-testid="prod-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div><Label>Categoria</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="prod-cat"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      {form.category && !CATEGORIES.includes(form.category) && <SelectItem value={form.category}>{form.category}</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Marca</Label><Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} /></div>
                <div><Label>Fornitore</Label>
                  <Select value={form.supplier_id || "none"} onValueChange={(v) => setForm({ ...form, supplier_id: v === "none" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Unità</Label><Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="kg, L, pz…" /></div>
                <div><Label>Quantità</Label><Input data-testid="prod-qty" type="number" step="0.01" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} /></div>
                <div><Label>Confezione (dim.)</Label><Input type="number" step="0.01" value={form.pack_size} onChange={(e) => setForm({ ...form, pack_size: e.target.value })} /></div>
                <div><Label>Costo confezione (€)</Label><Input type="number" step="0.01" value={form.pack_cost} onChange={(e) => setForm({ ...form, pack_cost: e.target.value })} /></div>
                <div><Label>Costo unitario (€)</Label><Input data-testid="prod-unit-cost" type="number" step="0.01" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} /></div>
                <div><Label>IVA %</Label><Input type="number" value={form.vat} onChange={(e) => setForm({ ...form, vat: e.target.value })} /></div>
                <div><Label>Scorta minima</Label><Input type="number" value={form.min_stock} onChange={(e) => setForm({ ...form, min_stock: e.target.value })} /></div>
                <div><Label>Scorta ideale</Label><Input type="number" value={form.ideal_stock} onChange={(e) => setForm({ ...form, ideal_stock: e.target.value })} /></div>
                <div><Label>Posizione</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
                <div><Label>Scadenza</Label><Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} /></div>
                <div className="col-span-2"><Label>Note</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>

                {/* Sprint 4.75: Reorder override */}
                <div className="col-span-2 border border-[#E2D9C8] rounded p-3 bg-[#FDFBF7] mt-2" data-testid="reorder-override-panel">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="label-eyebrow">Riordino — override prodotto (opzionale)</div>
                      <div className="text-[11px] text-neutral-500">Lasciare vuoto per ereditare da categoria o globale.</div>
                    </div>
                    {editing && (form.safety_days !== "" || form.target_days !== "") && (
                      <Button type="button" variant="ghost" size="sm" onClick={resetOverride} data-testid="reset-override-btn">
                        Ripristina valori ereditati
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Safety days</Label>
                      <Input type="number" min="0" value={form.safety_days}
                        onChange={(e) => setForm({ ...form, safety_days: e.target.value })}
                        placeholder={effectiveReorder ? `eredita ${effectiveReorder.safety} (${effectiveReorder.safety_source})` : "eredita"}
                        data-testid="prod-safety-days" />
                      {effectiveReorder && form.safety_days === "" && (
                        <div className="text-[11px] text-neutral-500 mt-1">
                          Effettivo: <b>{effectiveReorder.safety} giorni</b> — ereditato da {effectiveReorder.safety_source === "categoria" ? `categoria "${form.category}"` : "default globale"}
                        </div>
                      )}
                    </div>
                    <div>
                      <Label>Target days</Label>
                      <Input type="number" min="0" value={form.target_days}
                        onChange={(e) => setForm({ ...form, target_days: e.target.value })}
                        placeholder={effectiveReorder ? `eredita ${effectiveReorder.target} (${effectiveReorder.target_source})` : "eredita"}
                        data-testid="prod-target-days" />
                      {effectiveReorder && form.target_days === "" && (
                        <div className="text-[11px] text-neutral-500 mt-1">
                          Effettivo: <b>{effectiveReorder.target} giorni</b> — ereditato da {effectiveReorder.target_source === "categoria" ? `categoria "${form.category}"` : "default globale"}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <DialogFooter className="col-span-2"><Button data-testid="prod-submit" type="submit" className="bg-neutral-900 text-[#FDFBF7]">{editing ? "Salva modifiche" : "Salva"}</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          )}
          </div>
        } />

      <div className="flex gap-3 mb-4">
        <Input data-testid="inv-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca prodotto…" className="max-w-xs" />
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger data-testid="inv-cat" className="max-w-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le categorie</SelectItem>
            {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="border border-[#E2D9C8] rounded-lg bg-white overflow-x-auto">
        <table className="w-full text-sm zebra min-w-[720px]">
          <thead><tr className="text-left label-eyebrow">
            <th className="p-3">Prodotto</th><th className="p-3">Categoria</th><th className="p-3 text-right">Qtà</th>
            <th className="p-3 text-right">Min</th><th className="p-3 text-right">Costo unit.</th>
            <th className="p-3 text-right">Valore</th><th className="p-3">Scadenza</th><th className="p-3"></th>
          </tr></thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.id} className="border-t border-[#E2D9C8]" data-testid={`prod-${p.id}`}>
                <td className="p-3 font-medium">{p.name}{belowMin(p) && <AlertTriangle size={14} className="inline ml-2 text-red-700" />}</td>
                <td className="p-3 capitalize text-neutral-600">{p.category}</td>
                <td className={`p-3 text-right ${belowMin(p) ? "text-red-700 font-medium" : ""}`}>
                  <Input type="number" step="0.01" defaultValue={p.quantity}
                    data-testid={`inline-qty-${p.id}`}
                    onBlur={(e) => { const v = Number(e.target.value); if (v !== Number(p.quantity)) inlineUpdate(p, { quantity: v }); }}
                    className="w-20 h-7 text-right inline-block" />
                  <span className="ml-1 text-xs text-neutral-500">{p.unit}</span>
                </td>
                <td className="p-3 text-right text-neutral-500">{p.min_stock}</td>
                <td className="p-3 text-right">
                  <Input type="number" step="0.01" defaultValue={p.unit_cost}
                    data-testid={`inline-cost-${p.id}`}
                    onBlur={(e) => { const v = Number(e.target.value); if (v !== Number(p.unit_cost)) inlineUpdate(p, { unit_cost: v }); }}
                    className="w-24 h-7 text-right inline-block" />
                </td>
                <td className="p-3 text-right">{fmtEUR(Number(p.quantity) * Number(p.unit_cost))}</td>
                <td className="p-3 text-xs text-neutral-500">{p.expiry_date ? p.expiry_date.slice(0, 10) : "—"}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  <button onClick={() => openEdit(p)} data-testid={`edit-prod-${p.id}`} className={`text-neutral-500 mr-2 ${canWrite ? "hover:text-neutral-900" : "opacity-30 cursor-not-allowed"}`} disabled={!canWrite}><Pencil size={14} strokeWidth={1.5} /></button>
                  <button onClick={() => del(p.id)} data-testid={`del-prod-${p.id}`} className={`text-neutral-400 ${canWrite ? "hover:text-red-700" : "opacity-30 cursor-not-allowed"}`} disabled={!canWrite}><Trash2 size={14} strokeWidth={1.5} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Bulk import dialog */}
      <Dialog open={importOpen} onOpenChange={(v) => (v ? setImportOpen(true) : closeImport())}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif flex items-center gap-2"><FileSpreadsheet size={16} strokeWidth={1.5} /> Importa magazzino da CSV / Excel</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-sm text-neutral-600 border-l-2 border-[#D7C4A3] pl-3">
              Colonne accettate (IT/EN): <b>name</b>, <b>category</b>, brand, <b>unit</b>, quantity, pack_size, pack_cost, <b>unit_cost</b>, min_stock, ideal_stock, location, vat, supplier_name, expiry_date, notes. Separatori CSV: <code>,</code> o <code>;</code>.
            </div>
            <button type="button" onClick={downloadTemplate} data-testid="download-inv-template"
              className="inline-flex items-center gap-1 text-xs px-3 py-1.5 border border-[#E2D9C8] rounded hover:border-neutral-900">
              <Download size={12} strokeWidth={1.5} /> Scarica template CSV
            </button>
            <div>
              <Label>File (CSV o XLSX)</Label>
              <Input data-testid="import-inv-file" ref={importInputRef} type="file" accept=".csv,.xlsx,.xlsm"
                onChange={(e) => { setImportFile(e.target.files?.[0]); setImportResult(null); }} />
              {importFile && <div className="text-xs text-neutral-500 mt-1">{importFile.name} · {(importFile.size / 1024).toFixed(1)} KB</div>}
            </div>
            <div>
              <Label>Modalità</Label>
              <Select value={importMode} onValueChange={setImportMode}>
                <SelectTrigger data-testid="import-inv-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="append">Aggiungi tutti come nuovi</SelectItem>
                  <SelectItem value="update">Aggiorna esistenti per nome (nuovi aggiunti)</SelectItem>
                  <SelectItem value="replace">Sostituisci tutto il magazzino</SelectItem>
                </SelectContent>
              </Select>
              {importMode === "replace" && (
                <div className="text-xs text-red-700 mt-1">⚠ Tutti i prodotti attuali verranno eliminati prima dell'import.</div>
              )}
              {importMode === "update" && (
                <div className="text-xs text-neutral-500 mt-1">I prodotti con lo stesso nome vengono aggiornati (quantità, costo, IVA…). I nuovi vengono aggiunti.</div>
              )}
            </div>
            {importResult && (
              <div className={`border rounded-lg p-3 text-sm ${importResult.errors?.length ? "border-red-300 bg-red-50" : importResult.warnings?.length ? "border-amber-300 bg-amber-50" : "border-emerald-300 bg-emerald-50"}`} data-testid="import-inv-result">
                <div className="font-medium">
                  {importResult.imported} nuovi · {importResult.updated || 0} aggiornati · su {importResult.total_rows} righe
                  {importResult.replaced > 0 && ` · ${importResult.replaced} eliminati`}
                </div>
                {importResult.errors?.length > 0 && (
                  <div className="mt-2 text-xs">
                    <div className="font-medium text-red-800 mb-1">Errori bloccanti ({importResult.errors.length}):</div>
                    <ul className="max-h-32 overflow-y-auto space-y-0.5">
                      {importResult.errors.slice(0, 20).map((err, i) => (
                        <li key={i}>Riga {err.row}{err.name ? ` (${err.name})` : ""}: {err.error}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {importResult.warnings?.length > 0 && (
                  <div className="mt-2 text-xs">
                    <div className="font-medium text-amber-900 mb-1">Avvisi ({importResult.warnings.length}):</div>
                    <ul className="max-h-32 overflow-y-auto space-y-0.5">
                      {importResult.warnings.slice(0, 20).map((w, i) => (
                        <li key={i}>Riga {w.row}{w.name ? ` (${w.name})` : ""}: {w.warning}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeImport}>{importResult ? "Chiudi" : "Annulla"}</Button>
            {!importResult && (
              <Button onClick={runImport} disabled={!importFile || importing}
                data-testid="import-inv-submit" className="bg-neutral-900 text-[#FDFBF7]">
                <Upload size={14} strokeWidth={1.5} /> {importing ? "Importazione…" : "Importa"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
